'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const ENDPOINTS = [
  { method: 'GET', path: '/api/v1/projections', description: 'Projections pour tous les matchs du jour', plan: 'PRO', calls: 45678, latency: 89 },
  { method: 'GET', path: '/api/v1/projections/{gameId}', description: 'Projection détaillée pour un match', plan: 'PRO', calls: 23456, latency: 67 },
  { method: 'GET', path: '/api/v1/teams/{teamId}/stats', description: 'Statistiques avancées d\'une équipe', plan: 'FREE', calls: 89234, latency: 45 },
  { method: 'GET', path: '/api/v1/history', description: 'Historique des projections et résultats', plan: 'PRO', calls: 12345, latency: 134 },
  { method: 'GET', path: '/api/v1/live', description: 'Données live des matchs en cours', plan: 'ELITE', calls: 34567, latency: 23 },
  { method: 'GET', path: '/api/v1/models', description: 'Performances des modèles ML', plan: 'ELITE', calls: 5678, latency: 56 },
  { method: 'POST', path: '/api/v1/projections/custom', description: 'Projection personnalisée avec paramètres', plan: 'ENTERPRISE', calls: 1234, latency: 234 },
  { method: 'GET', path: '/api/v1/alerts', description: 'Alertes intelligentes temps réel', plan: 'PRO', calls: 18923, latency: 34 },
];

const USAGE_DATA = [
  { hour: '00h', calls: 1234, errors: 12 },
  { hour: '04h', calls: 456, errors: 3 },
  { hour: '08h', calls: 3456, errors: 28 },
  { hour: '12h', calls: 8934, errors: 67 },
  { hour: '16h', calls: 12456, errors: 89 },
  { hour: '20h', calls: 18923, errors: 134 },
  { hour: '22h', calls: 15678, errors: 98 },
];

const API_KEYS = [
  { name: 'Production App', prefix: 'nhl_pk_live_', plan: 'ELITE', calls: 45678, limit: 10000, status: 'active', created: '2026-01-15' },
  { name: 'Dev Environment', prefix: 'nhl_pk_test_', plan: 'PRO', calls: 1234, limit: 1000, status: 'active', created: '2026-02-01' },
  { name: 'Analytics Dashboard', prefix: 'nhl_pk_live_', plan: 'PRO', calls: 8923, limit: 1000, status: 'active', created: '2026-03-10' },
];

const TOOLTIP_STYLE = { backgroundColor: '#111827', border: '1px solid #1E2D4A', borderRadius: '6px', color: '#E8EDF5', fontSize: '11px' };

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-positive-subtle text-positive',
  POST: 'bg-primary/10 text-primary',
  PUT: 'bg-warning/10 text-warning',
  DELETE: 'bg-negative/10 text-negative',
};

const PLAN_COLORS: Record<string, string> = {
  FREE: 'text-muted-foreground',
  PRO: 'text-primary',
  ELITE: 'text-warning',
  ENTERPRISE: 'text-purple-400',
};

export default function APIPlatformClient() {
  const [activeTab, setActiveTab] = useState<'docs' | 'keys' | 'usage'>('docs');
  const [showNewKey, setShowNewKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">NHL Analytics API Platform</h1>
          <p className="text-sm text-muted-foreground mt-0.5">API publique · Rate limiting · Quotas · Documentation Swagger · Monitoring</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="status-badge bg-positive-subtle text-positive border border-positive/20 font-mono-data">v1.0 · LIVE</span>
          <div className="flex items-center gap-2 bg-card border border-border rounded-lg p-1">
            {(['docs', 'keys', 'usage'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${activeTab === tab ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {tab === 'docs' ? 'Documentation' : tab === 'keys' ? 'API Keys' : 'Usage'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Appels Aujourd\'hui', value: '125,678', sub: '+8.2% vs hier', color: 'text-primary' },
          { label: 'Latence P95', value: '245ms', sub: 'Objectif <500ms', color: 'text-positive' },
          { label: 'Taux d\'Erreur', value: '0.12%', sub: 'Objectif <1%', color: 'text-positive' },
          { label: 'API Keys Actives', value: '234', sub: '18 nouveaux ce mois', color: 'text-foreground' },
        ].map((stat) => (
          <div key={stat.label} className="card p-4">
            <div className="text-xs text-muted-foreground mb-1">{stat.label}</div>
            <div className={`text-2xl font-bold font-mono-data ${stat.color}`}>{stat.value}</div>
            <div className="text-2xs text-muted-foreground mt-1">{stat.sub}</div>
          </div>
        ))}
      </div>

      {activeTab === 'docs' && (
        <div className="space-y-4">
          {/* Base URL */}
          <div className="card p-4">
            <div className="flex items-center gap-3 mb-3">
              <Icon name="GlobeAltIcon" size={16} className="text-primary" />
              <span className="text-sm font-semibold text-foreground">Base URL</span>
            </div>
            <div className="font-mono-data text-sm bg-card-hover p-3 rounded border border-border text-primary">
              https://nhlanalyt6360.builtwithrocket.new/api/v1
            </div>
          </div>

          {/* Endpoints */}
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4">Endpoints Disponibles</h3>
            <div className="space-y-2">
              {ENDPOINTS.map((ep, i) => (
                <div key={i} className="p-3 rounded-lg bg-card-hover border border-border/50 hover:border-border transition-colors">
                  <div className="flex items-center gap-3 mb-1">
                    <span className={`status-badge text-2xs font-bold ${METHOD_COLORS[ep.method]}`}>{ep.method}</span>
                    <span className="font-mono-data text-sm text-foreground">{ep.path}</span>
                    <span className={`ml-auto text-2xs font-bold ${PLAN_COLORS[ep.plan]}`}>{ep.plan}+</span>
                  </div>
                  <div className="flex items-center gap-4 text-2xs text-muted-foreground">
                    <span>{ep.description}</span>
                    <span className="ml-auto font-mono-data">{ep.calls.toLocaleString()} appels/jour</span>
                    <span className="font-mono-data">{ep.latency}ms avg</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Rate Limits */}
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4">Rate Limits & Quotas</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border">
                    {['Plan', 'Req/Minute', 'Req/Jour', 'Quota Mensuel', 'Endpoints'].map((h) => (
                      <th key={h} className="text-left py-2 px-3 text-muted-foreground font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { plan: 'FREE', rpm: 10, rpd: 100, monthly: '1,000', endpoints: 'teams/stats' },
                    { plan: 'PRO', rpm: 60, rpd: 1000, monthly: '10,000', endpoints: 'projections, history, alerts' },
                    { plan: 'ELITE', rpm: 300, rpd: 10000, monthly: '100,000', endpoints: 'live, models + tout PRO' },
                    { plan: 'ENTERPRISE', rpm: 1000, rpd: -1, monthly: 'Illimité', endpoints: 'Tous + custom' },
                  ].map((row) => (
                    <tr key={row.plan} className="border-b border-border/50">
                      <td className={`py-2 px-3 font-bold ${PLAN_COLORS[row.plan]}`}>{row.plan}</td>
                      <td className="py-2 px-3 font-mono-data text-foreground">{row.rpm}</td>
                      <td className="py-2 px-3 font-mono-data text-foreground">{row.rpd === -1 ? '∞' : row.rpd.toLocaleString()}</td>
                      <td className="py-2 px-3 font-mono-data text-foreground">{row.monthly}</td>
                      <td className="py-2 px-3 text-muted-foreground">{row.endpoints}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'keys' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-semibold text-foreground">Vos API Keys</h3>
            <button onClick={() => setShowNewKey(!showNewKey)} className="btn-primary flex items-center gap-2 text-sm px-4 py-2">
              <Icon name="PlusIcon" size={16} />
              Créer une clé
            </button>
          </div>

          {showNewKey && (
            <div className="card p-4 border-primary/30">
              <h4 className="text-sm font-semibold text-foreground mb-3">Nouvelle API Key</h4>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="Nom de la clé (ex: Production App)"
                  className="flex-1 bg-card-hover border border-border rounded px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
                />
                <button className="btn-primary px-4 py-2 text-sm">Générer</button>
                <button onClick={() => setShowNewKey(false)} className="btn-ghost px-4 py-2 text-sm">Annuler</button>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {API_KEYS.map((key, i) => (
              <div key={i} className="card p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-sm font-semibold text-foreground">{key.name}</span>
                    <span className={`ml-2 text-xs font-bold ${PLAN_COLORS[key.plan]}`}>{key.plan}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="status-badge bg-positive-subtle text-positive">active</span>
                    <button className="btn-ghost p-1.5">
                      <Icon name="EyeIcon" size={14} className="text-muted-foreground" />
                    </button>
                    <button className="btn-ghost p-1.5">
                      <Icon name="TrashIcon" size={14} className="text-negative" />
                    </button>
                  </div>
                </div>
                <div className="font-mono-data text-sm bg-card-hover p-2 rounded border border-border text-muted-foreground mb-3">
                  {key.prefix}••••••••••••••••
                </div>
                <div className="flex items-center gap-4 text-2xs">
                  <span className="text-muted-foreground">Appels: <span className="font-mono-data text-foreground">{key.calls.toLocaleString()}</span></span>
                  <span className="text-muted-foreground">Limite/jour: <span className="font-mono-data text-foreground">{key.limit.toLocaleString()}</span></span>
                  <span className="text-muted-foreground">Créée: <span className="text-foreground">{key.created}</span></span>
                </div>
                <div className="mt-2">
                  <div className="h-1.5 bg-card-hover rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, (key.calls / key.limit) * 100)}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'usage' && (
        <div className="space-y-4">
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4">Volume d&apos;Appels (24h)</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={USAGE_DATA}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E2D4A" />
                <XAxis dataKey="hour" tick={{ fill: '#6B7A99', fontSize: 10 }} />
                <YAxis tick={{ fill: '#6B7A99', fontSize: 10 }} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="calls" fill="#00D4FF" radius={[2, 2, 0, 0]} name="Appels" />
                <Bar dataKey="errors" fill="#FF4D4D" radius={[2, 2, 0, 0]} name="Erreurs" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4">Top Endpoints par Volume</h3>
            <div className="space-y-2">
              {ENDPOINTS.sort((a, b) => b.calls - a.calls).slice(0, 5).map((ep, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className={`status-badge text-2xs ${METHOD_COLORS[ep.method]}`}>{ep.method}</span>
                  <span className="font-mono-data text-xs text-foreground flex-1 truncate">{ep.path}</span>
                  <div className="w-32 h-1.5 bg-card-hover rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${(ep.calls / 89234) * 100}%` }} />
                  </div>
                  <span className="font-mono-data text-xs text-muted-foreground w-20 text-right">{ep.calls.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
